import {
  DELETION,
  EFFECT,
  EFFECTONCE,
  LAYOUT,
  LAYOUTONCE,
  NOEFFECT,
} from "../constants";
import { depsChanged, equals, extractFn, isFn, shallowEqual } from "../utils";
import { scheduleUpdateOnFiber, startTransition } from "../schedule";
import {
  DependencyList,
  Dispatch,
  DispatchWithoutAction,
  Effect,
  EffectCallback,
  Fiber,
  FunctionComponent,
  Hook,
  MutableRefObject,
  Queue,
  ReactNode,
  Reducer,
  ReducerAction,
  ReducerWithoutAction,
  Ref,
  SetStateAction,
  TransitionFunction,
  TransitionStartFunction,
  EFFECT_ONCE_TYPE,
  EFFECT_TYPE,
  Provider,
  ProviderProps,
} from "../types";

import { createListenable, Listenable, useListenable } from "./notifier";

let fiber: Fiber | undefined;
let workInProgressHook: Hook | undefined;
let currentHook: Hook | undefined;
let effectListIndex = 0;
let mounted = false;

export function getHook(): Hook {
  const newHook: Hook = {};

  if (mounted) {
    const nextCurrentHook =
      currentHook?.next || currentHook || fiber?.alternate?.hook;
    currentHook = nextCurrentHook;

    newHook.memoizedState = currentHook?.memoizedState;
    newHook.queue = currentHook?.queue;
  }

  if (workInProgressHook) {
    workInProgressHook.next = newHook;
    workInProgressHook = newHook;
  } else {
    fiber!.hook = workInProgressHook = newHook;
  }
  return workInProgressHook;
}

export function renderWithHooks(workInProgress: Fiber, Component: any): any {
  fiber = workInProgress;
  mounted = !!fiber.alternate;
  const children = Component(workInProgress.props);
  fiber = undefined;
  workInProgressHook = undefined;
  currentHook = undefined;
  effectListIndex = 0;
  return children;
}

export interface ContextType<T> extends Listenable {
  state: T;
  Provider: Provider<T>;
}

export function createContext<T>(defaultValue: T): ContextType<T> {
  const notifier = createListenable();
  let _state = defaultValue;
  const context = {
    ...notifier,
    get state(): T {
      return _state;
    },

    set state(newState: T) {
      if (equals(_state, newState)) return;
      _state = newState;
      notifier.notify(newState);
    },

    Provider: ({ value, children }: ProviderProps<T>) => {
      context.state = value;
      return children;
    },
  };
  return context;
}

function useContext<T>(context: ContextType<T>) {
  const listener = useListenable(context);
  return listener.state;
}

function checkForUpdate(hook?: Hook) {
  let nextHook = hook;
  while (nextHook) {
    if (nextHook.queue?.pending) {
      return true;
    }
    nextHook = nextHook.next;
  }
}

const memo = <T>(
  Component: FunctionComponent<T>,
  compare = Component.shouldUpdate || shallowEqual<T>
) => {
  let prevProps: T | undefined;
  let prevNode: Fiber | undefined;

  const comp = (props: T) => {
    getHook(); // need call it to currentHook not be undefined

    if (
      prevProps != undefined &&
      !checkForUpdate(currentHook) &&
      compare(prevProps, props)
    ) {
      return prevNode;
    }

    const nextNode = Component(props) as any;
    prevNode = nextNode;
    prevProps = props;

    return nextNode;
  };
  return comp;
};

function dispatchAction(currentFiber: Fiber, queue: Queue, action: any): void {
  const update: Hook = { action };
  const pending = queue.pending;

  if (pending == null) {
    update.next = update;
  } else {
    update.next = pending.next;
    pending.next = update;
  }

  queue.pending = update;

  const lastRenderedReducer = queue.lastRenderedReducer;
  const lastRenderedState = queue.lastRenderedState;

  const eagerState = lastRenderedReducer(lastRenderedState, action);
  update.eagerReducer = lastRenderedReducer;
  update.eagerState = eagerState;

  if (Object.is(eagerState, lastRenderedState)) return;
  scheduleUpdateOnFiber(currentFiber);
}

function reducerBase<
  R extends Reducer<any, any> | ReducerWithoutAction<any>,
  I
>(reducer: R, initialState: I): any {
  const hook = getHook();
  if (!mounted) {
    hook.memoizedState = initialState;

    hook.queue = {
      pending: null,
      lastRenderedReducer: reducer,
      lastRenderedState: initialState,
    };
  }

  const queue = hook.queue;
  const pendingQueue = queue?.pending;

  if (pendingQueue != null) {
    const current = currentHook;
    const first = pendingQueue.next;
    let newState = current!.memoizedState;
    let update = first;
    do {
      if (update?.eagerReducer === reducer) {
        newState = update?.eagerState;
      } else {
        const action = update?.action;
        newState = reducer(newState, action);
      }
      update = update?.next;
    } while (update && !equals(update, first));
    queue && (queue.pending = null);
    hook.memoizedState = newState;
    queue && (queue.lastRenderedState = newState);
  }

  const dispatch = dispatchAction.bind(null, fiber!, queue!);
  return [queue?.lastRenderedState, dispatch];
}

function useReducer<R extends Reducer<any, any> | ReducerWithoutAction<any>, I>(
  reducer: R,
  initialState: I
): [
  I,
  Dispatch<
    R extends Reducer<any, any> ? ReducerAction<R> : DispatchWithoutAction
  >
] {
  return reducerBase(reducer, initialState);
}

function useState<S = any>(
  initialState?: S | (() => S)
): [S, Dispatch<SetStateAction<S>>] {
  return reducerBase(extractFn, initialState);
}

function useRef<T>(current?: T): MutableRefObject<T | undefined> {
  return useMemo(() => ({ current }), []);
}

function useCallback<T extends Function>(callback: T, deps: DependencyList): T {
  return useMemo(() => callback, deps);
}

function useEffect(effect: EffectCallback, deps?: DependencyList): void {
  return useEffectBase(effect, deps, EFFECT, EFFECTONCE);
}

function useLayoutEffect(effect: EffectCallback, deps?: DependencyList): void {
  return useEffectBase(effect, deps, LAYOUT, LAYOUTONCE);
}

function pushEffect(
  tag: EFFECT_TYPE | EFFECT_ONCE_TYPE,
  create: Function,
  destroy: Function | undefined,
  deps: DependencyList | undefined,
  isUpdate: boolean
): Effect {
  return handleEffect(
    {
      tag,
      create,
      destroy,
      deps,
    },
    isUpdate
  );
}

function commitEffect(
  currentFiber: Fiber,
  fiberTag: any,
  effectType: EFFECT_TYPE,
  timesType: EFFECT_ONCE_TYPE
): void {
  const effectList = currentFiber.effect;
  if (!effectList) return;

  for (const effect of effectList) {
    if (equals(effect.tag, NOEFFECT)) continue;
    if (equals(fiberTag, DELETION) || equals(effect.tag, effectType)) {
      const destroy = effect.destroy;
      effect.destroy = undefined;
      if (destroy) {
        destroy();
      }
      if (equals(fiberTag, DELETION)) effect.tag = NOEFFECT;
    }
    if (equals(effect.tag, timesType)) {
      const create = effect.create;
      effect.destroy = create();
      effect.tag = NOEFFECT;
    }
    if (equals(effect.tag, effectType)) {
      const create = effect.create;
      effect.destroy = create();
    }
  }
}

export function dispatchEffects(currentFiber: Fiber, fiberTag: any) {
  startTransition(() =>
    commitEffect(currentFiber, fiberTag, EFFECT, EFFECTONCE)
  );
  commitEffect(currentFiber, fiberTag, LAYOUT, LAYOUTONCE);
}

export function handleEffect(effect?: Effect, isUpdate?: boolean) {
  if (!effect) {
    return fiber!.effect![effectListIndex];
  } else if (fiber?.effect || isUpdate) {
    fiber!.effect![effectListIndex] = effect;
  } else {
    fiber!.effect = [effect];
  }
  return effect;
}

function useEffectBase(
  cb: EffectCallback,
  deps: DependencyList | undefined,
  defaultTag: EFFECT_TYPE,
  onceTag: EFFECT_ONCE_TYPE
): void {
  if (mounted) {
    if (currentHook) {
      const prevEffect = handleEffect();
      const destroy = prevEffect.destroy;
      const prevDeps = prevEffect.deps;
      const dedupedTag: EFFECT_TYPE = depsChanged(deps, prevDeps)
        ? defaultTag
        : NOEFFECT;
      pushEffect(dedupedTag, cb, destroy, deps, true);
    }
  } else {
    const tag = deps?.length === 0 ? onceTag : defaultTag;
    pushEffect(tag, cb, undefined, deps, false);
  }
  effectListIndex++;
}

function useImperativeHandle<T, R extends T>(
  ref: Ref<T> | undefined,
  init: () => R,
  deps?: DependencyList
): void {
  return useMemo(() => {
    const instance = init();
    if (isFn(ref)) ref(instance);
    else if (ref) (ref as MutableRefObject<T>).current = instance;
  }, deps);
}

function useMemo(cb: Function, deps?: DependencyList): any {
  const hook = getHook();
  if (mounted) {
    if (depsChanged(hook.memoizedState.deps, deps)) {
      hook.memoizedState.res = cb();
      hook.memoizedState.deps = deps;
    }
  } else {
    hook.memoizedState = {
      res: cb(),
      deps,
    };
  }
  return hook.memoizedState.res;
}

function useTransition(): [boolean, TransitionStartFunction] {
  const [pending, setPending] = useState(false);
  const transitionStartFn = useCallback(
    (callback: TransitionFunction): void => {
      setPending(true);
      startTransition(() => {
        callback();
        setPending(false);
      });
    },
    []
  );
  return [pending, transitionStartFn];
}

export {
  useCallback,
  useContext,
  useEffect,
  // useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useTransition,
  memo,
};
